/** -------- Generic binary helpers -------- */
export function dataUrlToBlob(dataUrl: string): Blob {
    const m = dataUrl.match(/^data:([^;]+);base64,(.*)$/s);
    if (!m) throw new Error("Invalid data URL");
    const mime = m[1];
    const bin = atob(m[2]);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
}

export function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result));
        r.readAsDataURL(blob);
    });
}

/** Contain-fit helper to center inside target box */
export function fitContain(sw: number, sh: number, tw: number, th: number) {
    const s = Math.min(tw / sw, th / sh);
    const w = Math.round(sw * s);
    const h = Math.round(sh * s);
    const x = Math.round((tw - w) / 2);
    const y = Math.round((th - h) / 2);
    return { w, h, x, y };
}

/** Scale any dataUrl to a 1920x1080 JPEG (letterboxed), quality ~0.9 */
export async function scaleToMax1080(dataUrl: string): Promise<string> {
    const blob = dataUrl.startsWith("data:")
        ? dataUrlToBlob(dataUrl)
        : await (await fetch(dataUrl)).blob();
    const img = await createImageBitmap(blob);

    const MAX_W = 1920, MAX_H = 1080;
    const s = Math.min(MAX_W / img.width, MAX_H / img.height, 1);
    const w = Math.round(img.width * s);
    const h = Math.round(img.height * s);

    const canvas = Object.assign(document.createElement("canvas"), {
        width: w,
        height: h,
    }) as HTMLCanvasElement;

    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, w, h);

    return canvas.toDataURL("image/jpeg", 0.9);
}
/** Normalize an image source for upload:
 *  - If browser can decode -> convert to 1080p JPEG and return base64 + "jpeg"
 *  - If it can't (e.g. HEIC) -> keep original base64 and map correct 'type'
 */
export async function normalizeForUpload(
    src: string
): Promise<{ base64: string; type: "jpeg" | "png" | "webp" | "heic" }> {
    // Raw base64 without prefix (rare in our flow) -> assume jpeg
    if (!src.startsWith("data:")) {
        return { base64: src, type: "jpeg" };
    }

    const m = src.match(/^data:([^;]+);base64,(.*)$/s);
    if (!m) throw new Error("Invalid data URL");
    const mime = m[1].toLowerCase();
    const origBase64 = m[2];

    // Already JPEG -> send as-is
    if (mime === "image/jpeg" || mime === "image/jpg") {
        return { base64: origBase64, type: "jpeg" };
    }

    // Try to decode in browser and convert to 1080p JPEG
    try {
        const blob = src.startsWith("data:")
            ? dataUrlToBlob(src)
            : await (await fetch(src)).blob();
        const bmp = await createImageBitmap(blob); // may fail for HEIC
        const TW = 1920, TH = 1080;
        const { w, h, x, y } = fitContain(bmp.width, bmp.height, TW, TH);

        const canvas = Object.assign(document.createElement("canvas"), {
            width: TW,
            height: TH,
        }) as HTMLCanvasElement;

        const ctx = canvas.getContext("2d")!;
        ctx.fillStyle = "black";
        ctx.fillRect(0, 0, TW, TH);
        ctx.drawImage(bmp, x, y, w, h);

        const jpegUrl = canvas.toDataURL("image/jpeg", 0.9);
        const b64 = jpegUrl.split(",")[1]!;
        return { base64: b64, type: "jpeg" };
    } catch {
        const map: Record<string, "jpeg" | "png" | "webp" | "heic"> = {
            "image/jpeg": "jpeg",
            "image/jpg": "jpeg",
            "image/png": "png",
            "image/webp": "webp",
            "image/heic": "heic",
            "image/heif": "heic",
        };
        return { base64: origBase64, type: map[mime] ?? "jpeg" };
    }
}

/** Try to take a true still photo from the active track (full sensor) */
export async function takeStillPhotoFromTrack(
    webcamRef: React.RefObject<any>
): Promise<string | undefined> {
    try {
        const stream: MediaStream | undefined = webcamRef?.current?.stream;
        const track = stream?.getVideoTracks?.()[0];
        const ImageCaptureCtor = (window as any).ImageCapture;
        if (!track || !ImageCaptureCtor) return undefined;

        const ic = new ImageCaptureCtor(track);
        const blob: Blob = await ic.takePhoto();
        return await blobToDataUrl(blob);
    } catch {
        return undefined;
    }
}

/** Request better AF/AE/WB/zoom and reasonable min/ideal size (optional) */
export async function upgradeVideoTrackQuality(
    webcamRef: React.RefObject<any>
) {
    try {
        const stream: MediaStream | undefined = webcamRef?.current?.stream;
        const track = stream?.getVideoTracks?.()[0];
        if (!track?.getCapabilities || !track.applyConstraints) return;

        const caps: any = track.getCapabilities();
        const advanced: any[] = [];

        if (caps.focusMode?.includes("continuous")) advanced.push({ focusMode: "continuous" });
        if (caps.exposureMode?.includes("continuous")) advanced.push({ exposureMode: "continuous" });
        if (caps.whiteBalanceMode?.includes("continuous")) advanced.push({ whiteBalanceMode: "continuous" });
        if (typeof caps.zoom?.max === "number") {
            const z = Math.min(caps.zoom.max, Math.max(caps.zoom.min ?? 0, caps.zoom.max * 0.3));
            advanced.push({ zoom: z });
        }

        await track.applyConstraints({
            width: { min: 1280, ideal: 1920 },
            height: { min: 720, ideal: 1080 },
            aspectRatio: 16 / 9,
            advanced,
        });
    } catch {
        /* ignore */
    }
}

/** Constant 1080p constraints for react-webcam */
export const CONSTRAINTS_1080P: MediaTrackConstraints = {
    facingMode: { ideal: "environment" },
    width: { min: 1280, ideal: 1920 },
    height: { min: 720, ideal: 1080 },
    aspectRatio: 16 / 9,
};
